import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { maybeOfferBiometricSetup, persistToken, clearPersistedToken } from "../lib/biometricAuth";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("icard_token");
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      // Issue/refresh the SSE auth cookie so EventSource can authenticate
      // without putting the JWT in a URL query string. 60-minute lifetime —
      // re-issued on every app load + every refresh() call.
      api.post("/auth/sse-cookie").catch(() => {});
    } catch (e) {
      const s = e?.response?.status;
      if (s === 401 || s === 403) {
        // Token really is invalid/expired — sign out.
        localStorage.removeItem("icard_token");
        setUser(null);
      }
      // Network/proxy blip (cold start, 52x): KEEP the token so the user stays
      // signed in — the next successful call restores their session.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // After login OR registration, if the user scanned a physical iCaard before
  // authenticating, finish the activation by linking that serial to the new account.
  const _consumePendingLink = async () => {
    const serial = sessionStorage.getItem("icard_pending_link");
    if (!serial) return null;
    try {
      const { data } = await api.post("/me/link-physical-card", { serial });
      sessionStorage.removeItem("icard_pending_link");
      sessionStorage.removeItem("icard_activation_card");
      return data;
    } catch (e) {
      // Surface the error but DO NOT clear the pending — user can retry from /app
      console.warn("[auth] post-login card link failed:", e);
      return null;
    }
  };

  const login = async (phone, password, remember_me = false) => {
    const { data } = await api.post("/auth/login", { phone, password, remember_me });
    localStorage.setItem("icard_token", data.token);
    await persistToken(data.token);
    setUser(data.user);
    api.post("/auth/sse-cookie").catch(() => {});
    const linked = await _consumePendingLink();
    // Native only — one-time offer to enable Face ID / Touch ID / Fingerprint.
    maybeOfferBiometricSetup().catch(() => {});
    return { ...data, linked_card: linked };
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    localStorage.setItem("icard_token", data.token);
    await persistToken(data.token);
    setUser(data.user);
    // Registration already linked the card if `card_serial` was in the payload —
    // just clear the stashed serial so the post-auth toast doesn't fire twice.
    if (payload?.card_serial) {
      sessionStorage.removeItem("icard_pending_link");
      sessionStorage.removeItem("icard_activation_card");
    } else {
      await _consumePendingLink();
    }
    maybeOfferBiometricSetup().catch(() => {});
    return data;
  };

  const registerCustomer = async (payload) => {
    const {data} = await api.post('/auth/customer-signup',payload);
    localStorage.setItem('icard_token',data.token);
    await persistToken(data.token);
    setUser(data.user);
    // Card ownership is changed only by the explicit signup/link request.
    ['icard_pending_link','icard_activation_card','icard_activation_token'].forEach(key=>sessionStorage.removeItem(key));
    api.post('/auth/sse-cookie').catch(()=>{});
    return data;
  };

  const logout = async ({ keepPortal = true } = {}) => {
    try { await api.post("/auth/logout"); } catch {}
    try { await api.post("/auth/sse-cookie/clear"); } catch {}
    localStorage.removeItem("icard_token");
    clearPersistedToken().catch(() => {});
    // Persistent-portal contract: by default, KEEP `icard_portal` so the next
    // start drops the user back at the portal-specific login screen.  Pass
    // `keepPortal: false` to also clear the portal (used by the "Switch
    // portal" button on each login page).
    if (!keepPortal) {
      try { localStorage.removeItem("icard_portal"); } catch {}
    }
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, register, registerCustomer, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
